import * as React from 'react';
import i18n from 'i18next';
import _set from 'lodash/set';

// types
import { MetricDesignTunablesProps } from './types';
import { Widget } from '@sprinklr/stories/widget/Widget';
import { AnalyticsProjection } from '@sprinklr/stories/analytics/AnalyticsRequest';

// components
import LabelGroup from 'components/_UI/Forms/LabelGroup/LabelGroup';
import { Flexbox, Input, LabeledCheckbox, TooltipPopper } from 'components/spaceKit';

// tunables
import StatusTagTunables from 'components/Sidebar/RenderTunables/statusTagTunables/StatusTagTunables';

// styled
import { StyledCollapse } from '@sprinklr/display-builder/widgets/ComboChartWidget/styled-components';

// utils
import { getPrettyMetricLabel } from '@sprinklr/display-builder/widgets/DataTableWidget/helpers';
import { getMetricOptionsMap } from './utils/getMetricOptionsMap';
import { getMetricDesignOptionsKey } from './utils/getMetricDesignOptionsKey';
import { updateWidgetOptions } from 'utils/Widget/WidgetUtils';

export function MetricDesignTunables({
    widget,
    getMetricOptionsMapFn = getMetricOptionsMap,
    options = {},
}: MetricDesignTunablesProps) {
    const metricOptionsMap = getMetricOptionsMapFn(widget);

    function handleUsePrettyChange(
        checked: boolean,
        widget: Widget,
        metric: AnalyticsProjection,
        fallbackDecimals: number
    ) {
        const numberFormat = {
            decimals:
                widget.options[
                    `metricDesignOptions.${getMetricDesignOptionsKey(
                        metric.measurementName,
                        metric.aggregateFunction
                    )}.numberFormat`
                ]?.decimals ?? fallbackDecimals,
            usePretty: checked,
        };

        updateWidgetOptions(
            widget,
            `metricDesignOptions.${getMetricDesignOptionsKey(
                metric.measurementName,
                metric.aggregateFunction
            )}.numberFormat`,
            numberFormat
        );
    }

    function handleDecimalsChange(
        value: string,
        widget: Widget,
        metric: AnalyticsProjection,
        fallbackUsePretty: boolean
    ) {
        const eventValue = parseInt(value, 10);

        const numberFormat = {
            usePretty:
                widget.options[
                    `metricDesignOptions.${getMetricDesignOptionsKey(
                        metric.measurementName,
                        metric.aggregateFunction
                    )}.numberFormat`
                ]?.usePretty ?? fallbackUsePretty,
            decimals: eventValue,
        };

        updateWidgetOptions(
            widget,
            `metricDesignOptions.${getMetricDesignOptionsKey(
                metric.measurementName,
                metric.aggregateFunction
            )}.numberFormat`,
            numberFormat
        );
    }

    return (
        <Flexbox gap='l' vertical key='metricLayerTunables'>
            {widget.analyticsRequests?.[0]?.projections?.map((metric, index) => {
                const { usePretty, decimals } = metricOptionsMap(metric);
                const label = getPrettyMetricLabel(metric);
                return (
                    <StyledCollapse
                        label={
                            <TooltipPopper label={label}>
                                {
                                    i18n.t(
                                        'These options will override the column/row Metric Value Format options for this metric'
                                    ) as string
                                }
                            </TooltipPopper>
                        }
                        key={`${label}-${index}`}>
                        <LabeledCheckbox
                            label={i18n.t('Shortened Number (K,M,B)')}
                            checked={usePretty}
                            data-checked={usePretty}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                handleUsePrettyChange(
                                    event.target.checked,
                                    widget,
                                    metric,
                                    decimals
                                )
                            }
                        />
                        <LabelGroup key='widget.options.base.decimals' label={i18n.t('Decimals')}>
                            <Input
                                type='number'
                                data-id='numberFormat-decimals'
                                data-value={decimals}
                                value={decimals}
                                min={0}
                                max={10}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                    handleDecimalsChange(
                                        event.target.value,
                                        widget,
                                        metric,
                                        usePretty
                                    )
                                }
                            />
                        </LabelGroup>

                        {options?.statusTag ? (
                            <StatusTagTunables widget={widget} projection={metric} />
                        ) : null}
                    </StyledCollapse>
                );
            })}
        </Flexbox>
    );
}
